/**
 * Pure builder for the **edit handles** — the visible grab points the Edit tool
 * exposes on every annotation (REQ-UI-135, ADR-034 D7 amended).
 *
 * Before this, geometry editing existed but was invisible: the Move tool would
 * grab a line vertex within a pixel threshold, so reshaping worked only if you
 * already knew it was there. Handles make the affordance discoverable, and are
 * what the circle-resize and vertex-add capabilities hang off.
 *
 * Three handle kinds, distinguished by the ``handle_kind`` property so one
 * source can paint all of them with data-driven styling:
 *
 *   - ``vertex`` — a real position. Drag to move it, click to delete it.
 *   - ``midpoint`` — a *ghost* between two vertices. Drag to insert a new
 *     vertex there. Painted at lower opacity so it reads as latent rather than
 *     as an existing corner.
 *   - ``radius`` — the single handle at a circle's due-east edge. Drag in or
 *     out to resize. It sits at the outer end of the radius line (REQ-UI-134),
 *     so the line doubles as the affordance's own signpost.
 *
 * **Handles are emitted for every annotation, not just a selected one.** The
 * Edit tool makes *the shapes on the map* editable as a set — and gating on
 * selection would be self-defeating anyway, since activating a tool clears the
 * current selection (:mod:`./annotation-tool-store`).
 *
 * Markers get no handles: the pin *is* the drag target, and adding a dot on top
 * of it would only obscure the thing you are trying to grab.
 *
 * Pure (no map / no GL), so it is CI-testable; the layer that paints these is
 * verified in the §5.11 visual pass.
 *
 * Cross-references:
 *   - ADR-034 D7 (editing surface); feature-spec §8 (interaction model).
 *   - ./annotation-circle-geometry (circleRadiusLine — the radius handle's spot).
 *   - @/map/annotation-handle-layer (the paint path).
 */
import type { Feature, FeatureCollection, Point } from "geojson";
import type { Annotation } from "@/annotations/use-annotations";
import { circleRadiusLine } from "@/annotations/annotation-circle-geometry";

/** What a handle lets the operator do. Drives both paint and hit-routing. */
export type HandleKind = "vertex" | "midpoint" | "radius";

/** Build one handle feature. ``vertexIndex`` is the position a vertex handle
 *  moves, or — for a midpoint — the index of the segment's START vertex, which
 *  is where an inserted vertex goes. */
function handle(
  annotation: Annotation,
  kind: HandleKind,
  coordinates: number[],
  vertexIndex: number,
): Feature<Point> {
  return {
    type: "Feature",
    geometry: { type: "Point", coordinates },
    properties: {
      annotation_id: annotation.annotation_id,
      handle_kind: kind,
      vertex_index: vertexIndex,
      stroke_color: annotation.style.stroke_color ?? "#f5a623",
    },
  };
}

/** The planar midpoint of two positions. Planar rather than geodesic on
 *  purpose: this is a screen affordance sitting on the straight segment
 *  MapLibre actually draws between the two vertices, not a point on the
 *  great circle between them — a geodesic midpoint would sit visibly off
 *  the rendered line on a long or steep segment. */
function midpoint(a: readonly number[], b: readonly number[]): number[] {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/**
 * The handle source: every grab point for every annotation, as ``Point``
 * features tagged with ``handle_kind`` / ``vertex_index`` / ``annotation_id``.
 *
 * Callers paint this only while the Edit tool is active; the builder itself is
 * unconditional so it stays a pure function of the annotation list.
 */
export function annotationsToHandleFeatures(
  annotations: readonly Annotation[],
): FeatureCollection<Point> {
  const features: Feature<Point>[] = [];
  for (const annotation of annotations) {
    const geometry = annotation.geometry;

    if (annotation.kind === "circle") {
      if (annotation.radius_m != null && geometry.type === "Point") {
        const [lon, lat] = geometry.coordinates as number[];
        const [, edge] = circleRadiusLine(lon, lat, annotation.radius_m);
        features.push(handle(annotation, "radius", edge, 0));
      }
      continue;
    }

    // The positions a handle attaches to, and whether the shape closes back on
    // its first position (a polygon ring does; a line does not).
    let positions: number[][] | null = null;
    let closed = false;
    if (annotation.kind === "line" && geometry.type === "LineString") {
      positions = geometry.coordinates as number[][];
    } else if (annotation.kind === "polygon" && geometry.type === "Polygon") {
      const ring = (geometry.coordinates as number[][][])[0];
      // A GeoJSON ring repeats its first position last. That repeat is not a
      // corner — emitting a handle for it would stack two handles on one point,
      // and dragging the wrong one would tear the ring open.
      positions = ring.slice(0, -1);
      closed = true;
    }
    if (positions === null || positions.length === 0) {
      continue;
    }

    positions.forEach((position, index) => {
      features.push(handle(annotation, "vertex", [...position], index));
    });

    // One midpoint per segment. For a closed ring that includes the segment
    // from the last corner back to the first — it is a side like any other and
    // must be insertable into.
    const segmentCount = closed ? positions.length : positions.length - 1;
    for (let i = 0; i < segmentCount; i += 1) {
      const next = positions[(i + 1) % positions.length];
      features.push(handle(annotation, "midpoint", midpoint(positions[i], next), i));
    }
  }
  return { type: "FeatureCollection", features };
}
